import { useQuery } from '@tanstack/react-query';

import { useCurrentLocale } from './locale';
import { api } from './api';
import type {
  Accommodation,
  AccommodationType,
  Activity,
  ActivityType,
  Attraction,
  Brochure,
  Category,
  Collection,
  EventItem,
  Itinerary,
  Page,
  Post,
  PostType,
  Project,
  Region,
  Report,
  SearchResponse,
  Single,
  Trail,
  TrailDifficulty,
} from './types';

/**
 * TanStack Query hooks for the public API (§9.1).
 *
 * The active locale is part of every query key. The Axios client appends `?locale=` to the
 * request (see api.ts); putting the locale in the key as well means switching language
 * refetches and caches per-locale, instead of showing stale content from the previous
 * language until it expires.
 */

const REFERENCE_STALE = 30 * 60 * 1000; // reference data barely changes

export function useCategories() {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['categories', locale],
    queryFn: async () => (await api.get<Collection<Category>>('/categories')).data.data,
    staleTime: REFERENCE_STALE,
  });
}

export function useRegions() {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['regions', locale],
    queryFn: async () => (await api.get<Collection<Region>>('/regions')).data.data,
    staleTime: REFERENCE_STALE,
  });
}

export type AttractionFilters = {
  region?: string;
  category?: string;
  q?: string;
  is_featured?: boolean;
};

export function useAttractions(filters: AttractionFilters = {}) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['attractions', locale, filters],
    queryFn: async () =>
      (await api.get<Collection<Attraction>>('/attractions', { params: filters })).data.data,
  });
}

// ── Activities & Tours (§6.6–6.7, §7.6–7.9) ─────────────────────────────────

export function useActivityTypes(onHub?: boolean) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['activity-types', locale, onHub],
    queryFn: async () =>
      (await api.get<Collection<ActivityType>>('/activity-types', { params: onHub ? { on_hub: 1 } : {} }))
        .data.data,
    staleTime: REFERENCE_STALE,
  });
}

export function useActivityType(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['activity-type', locale, slug],
    queryFn: async () => (await api.get<Single<ActivityType>>(`/activity-types/${slug}`)).data.data,
  });
}

export function useActivities(type?: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['activities', locale, type],
    queryFn: async () =>
      (await api.get<Collection<Activity>>('/activities', { params: type ? { type } : {} })).data.data,
  });
}

export function useActivity(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['activity', locale, slug],
    queryFn: async () => (await api.get<Single<Activity>>(`/activities/${slug}`)).data.data,
  });
}

// ── Trails (§6.5, §7.10–7.11) ───────────────────────────────────────────────

export type TrailFilters = { region?: string; difficulty?: TrailDifficulty; route_type?: string; q?: string };

export function useTrails(filters: TrailFilters = {}) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['trails', locale, filters],
    queryFn: async () => (await api.get<Collection<Trail>>('/trails', { params: filters })).data.data,
  });
}

export function useTrail(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['trail', locale, slug],
    queryFn: async () => (await api.get<Single<Trail>>(`/trails/${slug}`)).data.data,
  });
}

// ── Events (§6.9, §7.12–7.13) ───────────────────────────────────────────────

export type EventFilters = { from?: string; to?: string; view?: 'list' | 'calendar' };

export function useEvents(filters: EventFilters = {}) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['events', locale, filters],
    queryFn: async () => (await api.get<Collection<EventItem>>('/events', { params: filters })).data.data,
  });
}

export function useEvent(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['event', locale, slug],
    queryFn: async () => (await api.get<Single<EventItem>>(`/events/${slug}`)).data.data,
  });
}

// ── Stay & Eat (§6.8, §7.14) ────────────────────────────────────────────────

export function useAccommodations(type?: AccommodationType) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['accommodations', locale, type],
    queryFn: async () =>
      (await api.get<Collection<Accommodation>>('/accommodations', { params: type ? { type } : {} }))
        .data.data,
  });
}

export function useAccommodation(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['accommodation', locale, slug],
    queryFn: async () => (await api.get<Single<Accommodation>>(`/accommodations/${slug}`)).data.data,
  });
}

// ── Posts — News (§7.17), Blog (§7.18), Home Highlights (§7.1) ───────────────

export function usePosts(type?: PostType, limit?: number) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['posts', locale, type, limit],
    queryFn: async () =>
      (await api.get<Collection<Post>>('/posts', { params: { ...(type ? { type } : {}), ...(limit ? { limit } : {}) } }))
        .data.data,
  });
}

export function usePost(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['post', locale, slug],
    queryFn: async () => (await api.get<Single<Post>>(`/posts/${slug}`)).data.data,
  });
}

// ── Projects — DMO project write-ups (§6.11, §7.17) ─────────────────────────

export function useProjects() {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['projects', locale],
    queryFn: async () => (await api.get<Collection<Project>>('/projects')).data.data,
  });
}

export function useProject(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['project', locale, slug],
    queryFn: async () => (await api.get<Single<Project>>(`/projects/${slug}`)).data.data,
  });
}

// ── Reports (§6.12, §7.17) + Brochures (§6.13, §7.15) ───────────────────────

export function useReports() {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['reports', locale],
    queryFn: async () => (await api.get<Collection<Report>>('/reports')).data.data,
  });
}

export function useBrochures() {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['brochures', locale],
    queryFn: async () => (await api.get<Collection<Brochure>>('/brochures')).data.data,
  });
}

// ── CMS Pages (§6.14, §7.2/§7.3/§7.19) ──────────────────────────────────────

export function usePages(group?: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['pages', locale, group],
    queryFn: async () =>
      (await api.get<Collection<Page>>('/pages', { params: group ? { group } : {} })).data.data,
  });
}

export function usePage(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['page', locale, slug],
    queryFn: async () => (await api.get<Single<Page>>(`/pages/${slug}`)).data.data,
  });
}

// ── Itineraries / Tours (§6.15, §7.16) ──────────────────────────────────────

export function useItineraries() {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['itineraries', locale],
    queryFn: async () => (await api.get<Collection<Itinerary>>('/itineraries')).data.data,
  });
}

export function useItinerary(slug: string) {
  const locale = useCurrentLocale();
  return useQuery({
    queryKey: ['itinerary', locale, slug],
    queryFn: async () => (await api.get<Single<Itinerary>>(`/itineraries/${slug}`)).data.data,
  });
}

// ── Global search (§14) ─────────────────────────────────────────────────────

/**
 * One request covers all six searchable entity types. `enabled` keeps the query idle until the
 * term is long enough, so type-ahead does not fire a request per keystroke on a bare box; the
 * caller is expected to debounce the term it passes in.
 */
export function useSearch(term: string, minLength = 2) {
  const locale = useCurrentLocale();
  const trimmed = term.trim();
  return useQuery({
    queryKey: ['search', locale, trimmed],
    enabled: trimmed.length >= minLength,
    queryFn: async () => (await api.get<Single<SearchResponse>>('/search', { params: { q: trimmed } })).data.data,
  });
}
